/**
 * /downhole/invoices/ready — what is waiting to be billed: every billable run
 * (closed or not-run, on no live invoice), one panel per customer, each run
 * with what stands between it and an invoice.
 *
 * "Ready to invoice" in the sidebar opens THIS page (Jason, 2026-10-30). The
 * list of invoices already made is Finance → Invoices (and /downhole/invoices);
 * a sidebar item that opened a list of invoices under this name said one thing
 * and did another.
 *
 * NO NEW API. It reads the same `GET /downhole/runs` the generate dialog reads,
 * filters with the same `isBillable` / `isInvoiceable`, and a customer's
 * "Create invoice" opens that dialog (`GenerateInvoiceDialog`) preset with the
 * customer's runs whose ticket the customer has approved. The dialog keeps the
 * one-currency check, the refusals, and opening the new invoice on its own
 * page — none of that is copied here.
 *
 * A run with no customer cannot be invoiced (the API bills one customer per
 * invoice); it is listed under its own heading with no button, so it is not
 * silently dropped.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Receipt } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeRuns } from '../../hooks/useDownhole';
import { canWriteDownholeInvoices } from '../../lib/downhole-roles';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { DhRun } from '../../types/downhole';
import { DhEmpty, DhPanel } from './DownholeBits';
import { GenerateInvoiceDialog, isBillable, isInvoiceable, ticketGate } from './InvoiceDialogs';
import { useBackToList } from './recordPage';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const NO_CUSTOMER = '__none__';

type Group = { key: string; name: string; runs: DhRun[]; ready: DhRun[] };

const runLabel = (r: DhRun) => `${r.assembly_number || r.assembly_name || 'Assembly'} · Run ${r.run_number ?? '—'}`;

function GroupPanel({ group, canWrite, onCreate }: { group: Group; canWrite: boolean; onCreate: (ids: string[]) => void }) {
  const noCustomer = group.key === NO_CUSTOMER;
  return (
    <DhPanel
      eyebrow={noCustomer ? 'Cannot be invoiced yet' : `${group.ready.length} of ${group.runs.length} ready`}
      title={group.name}
      testId="dh-ready-group"
      actions={
        canWrite && !noCustomer && group.ready.length > 0 ? (
          <button type="button" className={PRIMARY} onClick={() => onCreate(group.ready.map((r) => r.id))} data-testid="dh-ready-create">
            <Receipt className="size-4" aria-hidden="true" />
            Create invoice
          </button>
        ) : undefined
      }
      footer={noCustomer ? 'A run needs a customer before it can go on an invoice — set it on the assembly.' : undefined}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr>
              <th scope="col" className="px-4 py-3">Run</th>
              <th scope="col" className="px-4 py-3">Well</th>
              <th scope="col" className="px-4 py-3">Ticket</th>
              <th scope="col" className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {group.runs.map((r) => {
              const gate = ticketGate(r);
              return (
                <tr key={r.id} className={`border-t border-l-4 border-fieldiq-border ${gate ? 'border-l-transparent' : 'border-l-fi-green'}`} data-testid="dh-ready-run">
                  <td className="px-4 py-2.5">
                    <Link to={`/downhole/assemblies/${r.assembly_id}`} className="font-mono text-sm font-extrabold text-white underline-offset-2 hover:underline">
                      {runLabel(r)}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-fieldiq-text">{r.well_name || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-sm text-fieldiq-text">
                    {r.dh_ticket_id ? <Link to={`/downhole/tickets/${r.dh_ticket_id}`} className="underline-offset-2 hover:underline">{r.ticket_number || 'Open ticket'}</Link> : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-sm" data-testid="dh-ready-status">
                    {gate ? (
                      <span className="text-fieldiq-muted">
                        Not yet — {gate.text}.{' '}
                        <Link to={gate.to} className="text-fieldiq-text underline underline-offset-2">{gate.link}</Link>
                      </span>
                    ) : (
                      <span className="font-semibold text-fi-green">Ready — ticket approved by the customer</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DhPanel>
  );
}

export default function DownholeReadyToInvoiceScreen() {
  const back = useBackToList('/downhole/invoices');
  const { user } = useAuth();
  const canWrite = canWriteDownholeInvoices(user?.role);
  const runsQuery = useDownholeRuns();
  const failure = queryLoadFailure(runsQuery);
  const tz = runsQuery.data?.meta.time_zone ?? null;
  const [preset, setPreset] = useState<string[] | null>(null);

  const groups = useMemo<Group[]>(() => {
    const m = new Map<string, Group>();
    for (const r of (runsQuery.data?.data ?? []).filter(isBillable)) {
      const key = r.customer_id ?? NO_CUSTOMER;
      const g = m.get(key) ?? { key, name: r.customer_id ? r.customer_name || 'Unnamed customer' : 'No customer set', runs: [], ready: [] };
      g.runs.push(r);
      if (isInvoiceable(r)) g.ready.push(r);
      m.set(key, g);
    }
    for (const g of Array.from(m.values())) {
      g.runs.sort((a, b) => Number(isInvoiceable(b)) - Number(isInvoiceable(a)) || runLabel(a).localeCompare(runLabel(b)));
    }
    // Customers with something ready first, then by name; the no-customer group last.
    return Array.from(m.values()).sort(
      (a, b) =>
        Number(a.key === NO_CUSTOMER) - Number(b.key === NO_CUSTOMER) ||
        Number(b.ready.length > 0) - Number(a.ready.length > 0) ||
        a.name.localeCompare(b.name),
    );
  }, [runsQuery.data]);

  const loaded = !runsQuery.isLoading && !failure;
  const readyCount = groups.reduce((n, g) => n + (g.key === NO_CUSTOMER ? 0 : g.ready.length), 0);
  const waitingCount = groups.reduce((n, g) => n + g.runs.length, 0) - readyCount;

  return (
    <div data-testid="dh-ready-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Ready to invoice"
        onBack={back}
        backLabel="Back to invoices"
        stats={[
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Ready', sub: 'Ticket approved, not billed', tone: 'accent', loading: runsQuery.isLoading, value: displayValue(loaded ? readyCount : undefined) },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Not yet', sub: 'Billable, ticket not approved', tone: 'neutral', loading: runsQuery.isLoading, value: displayValue(loaded ? waitingCount : undefined) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            Closed runs that no invoice bills yet, by customer. A run can be invoiced once the customer has approved its field ticket; the rest say what they are waiting on.
          </p>
          {!canWrite && groups.length > 0 && <p className="text-sm text-fieldiq-muted">Only an admin or office staff can create an invoice.</p>}
          {runsQuery.isLoading ? (
            <p className="px-4 py-10 text-center text-sm text-fieldiq-muted">Loading runs ready to invoice…</p>
          ) : failure ? (
            <DhPanel eyebrow="Billable runs" title="Ready to invoice">
              <LoadFailureNotice error={queryFailureError(runsQuery)} kind={failure} subject="runs ready to invoice" />
            </DhPanel>
          ) : groups.length === 0 ? (
            <DhPanel eyebrow="Billable runs" title="Ready to invoice" testId="dh-ready-empty">
              <DhEmpty>No closed runs are waiting to be invoiced.</DhEmpty>
            </DhPanel>
          ) : (
            groups.map((g) => <GroupPanel key={g.key} group={g} canWrite={canWrite} onCreate={setPreset} />)
          )}
        </div>
      </section>
      {canWrite && preset && <GenerateInvoiceDialog open onClose={() => setPreset(null)} tz={tz} presetRunIds={preset} />}
    </div>
  );
}
